/**
 * Sezione "Siti" nel dettaglio cliente (Slice A — Forniture).
 *
 * Mostra la lista dei siti del cliente (nome, indirizzo, data creazione) e,
 * solo per chi ha PORTFOLIO_MANAGEMENT, un form per aggiungerne uno nuovo e un
 * pulsante "Rimuovi" per ogni riga. Gli utenti con sola VIEW_PORTFOLIO vedono
 * la lista in sola lettura (pattern coerente con OwnershipSection).
 */

import { useState, type FormEvent } from 'react';
import { usePermissions } from '../../auth/index.js';
import { useSites, useAddSite, useRemoveSite } from './hooks.js';
import type { CreateSiteInput } from './types.js';
import styles from './Customers.module.css';

/** Form per aggiungere un sito al cliente. */
function NuovoSitoForm({ customerId }: { customerId: number }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const { mutate, isPending, isError, error } = useAddSite(customerId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const input: CreateSiteInput = {
      name: name.trim(),
      ...(address.trim() ? { address: address.trim() } : {}),
    };
    mutate(input, {
      onSuccess: () => {
        setName('');
        setAddress('');
      },
    });
  }

  function messaggioErrore(): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return "Errore durante l'aggiunta del sito.";
  }

  return (
    <div className={styles.formCard} data-testid="form-aggiungi-sito">
      <p className={styles.formTitle}>Aggiungi sito</p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="sito-name">
              Nome *
            </label>
            <input
              id="sito-name"
              className={styles.formInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="es. Sede Milano"
              data-testid="input-sito-name"
            />
          </div>
          <div className={styles.formGroup} style={{ flex: 2 }}>
            <label className={styles.formLabel} htmlFor="sito-address">
              Indirizzo
            </label>
            <input
              id="sito-address"
              className={styles.formInput}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="es. Via Roma 1, 20100 Milano"
              data-testid="input-sito-address"
            />
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-aggiungi-sito"
          >
            {isPending ? 'Aggiunta…' : 'Aggiungi'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-aggiungi-sito">
              {messaggioErrore()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Sezione "Siti" del dettaglio cliente. */
export function SitesSection({ customerId }: { customerId: number }) {
  const { can } = usePermissions();
  const { data: siti, isLoading, isError, error } = useSites(customerId);
  const removeSite = useRemoveSite(customerId);

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  return (
    <div data-testid="sezione-siti">
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-siti">
          Caricamento siti…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-siti">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei siti'}
        </p>
      )}

      {!isLoading && !isError && siti !== undefined && siti.length === 0 && (
        <div className={styles.emptyState} data-testid="siti-vuoti">
          Nessun sito registrato per questo cliente.
        </div>
      )}

      {!isLoading && !isError && siti !== undefined && siti.length > 0 && (
        <table className={styles.table} data-testid="tabella-siti">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Indirizzo</th>
              <th>Aggiunto il</th>
              {puoGestire && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {siti.map((s) => (
              <tr key={s.id} data-testid={`sito-row-${s.id}`}>
                <td>{s.name}</td>
                <td>{s.address ?? '—'}</td>
                <td>{s.createdAt}</td>
                {puoGestire && (
                  <td>
                    <button
                      className={`${styles.btnDanger} ${styles.btnSmall}`}
                      onClick={() => removeSite.mutate(s.id)}
                      disabled={removeSite.isPending}
                      data-testid={`btn-rimuovi-sito-${s.id}`}
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {removeSite.isError && (
        <span className={styles.inlineMutError} data-testid="errore-rimuovi-sito">
          {removeSite.error instanceof Error
            ? removeSite.error.message
            : 'Errore durante la rimozione del sito.'}
        </span>
      )}

      {/* Form aggiunta sito (solo con PORTFOLIO_MANAGEMENT) */}
      {puoGestire && <NuovoSitoForm customerId={customerId} />}
    </div>
  );
}
